// Service pour gérer les tokens JWT et l'authentification
// La vérification JWT est maintenant côté serveur (Cloud Function) pour sécurité
import { signInWithCustomToken } from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';

const STORAGE_KEY = 'fc_auth_planification';

const getFunctionsRegion = (): string => {
  const region = import.meta.env.VITE_FUNCTIONS_REGION || 'us-central1';
  console.log('[JWT SERVICE] Region Functions:', region);
  return region;
};

const getVerifyJwtUrl = (): string => {
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
  console.log('[JWT SERVICE] Project ID:', projectId);
  if (!projectId) {
    console.error('[JWT SERVICE] VITE_FIREBASE_PROJECT_ID manquant');
    throw new Error('VITE_FIREBASE_PROJECT_ID manquant');
  }
  const region = getFunctionsRegion();
  const url = `https://${region}-${projectId}.cloudfunctions.net/verifyJwtAndCreateToken`;
  console.log('[JWT SERVICE] URL verifyJwtAndCreateToken:', url);
  return url;
};

export const hasAuthTokenInUrl = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  const hasToken = Boolean(urlParams.get('auth_token'));
  console.log('[JWT SERVICE] Token dans URL:', hasToken);
  return hasToken;
};

export const consumeAuthTokenFromUrl = (): string | null => {
  console.log('[JWT SERVICE] Tentative de récupération du token depuis URL');
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('auth_token');
  
  if (!token) {
    console.log('[JWT SERVICE] Aucun token trouvé dans URL');
    return null;
  }

  console.log('[JWT SERVICE] Token trouvé dans URL:', token.substring(0, 20) + '...');
  const url = new URL(window.location.href);
  url.searchParams.delete('auth_token');
  window.history.replaceState({}, document.title, url.toString());
  console.log('[JWT SERVICE] Token supprimé de l\'URL');

  return token;
};

export const getStoredAuthToken = (): string | null => {
  const token = localStorage.getItem(STORAGE_KEY);
  console.log('[JWT SERVICE] Token stocké (clé:', STORAGE_KEY + '):', token ? token.substring(0, 20) + '...' : 'null');
  return token;
};

export const setStoredAuthToken = (token: string): void => {
  console.log('[JWT SERVICE] Stockage du token (clé:', STORAGE_KEY + '):', token.substring(0, 20) + '...');
  localStorage.setItem(STORAGE_KEY, token);
  console.log('[JWT SERVICE] Token stocké avec succès');
};

export const clearJwtToken = (): void => {
  console.log('[JWT SERVICE] Suppression du token JWT (clé:', STORAGE_KEY + ')');
  localStorage.removeItem(STORAGE_KEY);
  console.log('[JWT SERVICE] Token supprimé');
};

type VerifyJwtResponse = {
  customToken: string;
  user: {
    uid: string;
    email: string;
    role: string;
  };
};

/**
 * Envoie le JWT brut à la Cloud Function pour vérification côté serveur
 * Retourne le customToken Firebase si le JWT est valide
 */
const callVerifyJwtAndCreateToken = async (jwtToken: string): Promise<VerifyJwtResponse> => {
  console.log('[JWT SERVICE] Appel verifyJwtAndCreateToken (vérification côté serveur)');
  const url = getVerifyJwtUrl();
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => {
    console.error('[JWT SERVICE] Timeout de 15s atteint pour verifyJwtAndCreateToken');
    controller.abort();
  }, 15000);
  
  try {
    console.log('[JWT SERVICE] Envoi du JWT à:', url);
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token: jwtToken }),
      signal: controller.signal,
    }).finally(() => {
      window.clearTimeout(timeoutId);
    });

    console.log('[JWT SERVICE] Réponse reçue - Status:', response.status, response.statusText);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Erreur inconnue' }));
      console.error('[JWT SERVICE] Erreur:', response.status, errorData);
      throw new Error(errorData.error || `verifyJwtAndCreateToken: ${response.status}`);
    }

    const data = (await response.json()) as VerifyJwtResponse;
    console.log('[JWT SERVICE] Données reçues - user:', data.user);
    
    if (!data.customToken) {
      console.error('[JWT SERVICE] customToken manquant dans la réponse');
      throw new Error('verifyJwtAndCreateToken: réponse invalide');
    }
    
    console.log('[JWT SERVICE] Custom token reçu:', data.customToken.substring(0, 20) + '...');
    return data;
  } catch (error: any) {
    console.error('[JWT SERVICE] Exception lors de callVerifyJwtAndCreateToken:', error);
    throw error;
  }
};

/**
 * Récupère le token JWT depuis l'URL ou le localStorage
 */
export const getJwtToken = (): string | null => {
  console.log('[JWT SERVICE] getJwtToken - Début');
  const tokenFromUrl = consumeAuthTokenFromUrl();
  if (tokenFromUrl) {
    console.log('[JWT SERVICE] Token récupéré depuis URL, stockage...');
    setStoredAuthToken(tokenFromUrl);
    return tokenFromUrl;
  }
  const storedToken = getStoredAuthToken();
  console.log('[JWT SERVICE] Token final:', storedToken ? 'Présent' : 'Absent');
  return storedToken;
};

export const isJwtTokenValid = async (): Promise<boolean> => {
  console.log('[JWT SERVICE] Vérification de la validité du token JWT');
  const token = getJwtToken();
  if (!token) {
    console.log('[JWT SERVICE] Aucun token à vérifier');
    return false;
  }
  
  try {
    // La vérification se fait maintenant côté serveur
    // On vérifie juste que le token existe et n'est pas expiré (décodage basique)
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.log('[JWT SERVICE] Format JWT invalide');
      return false;
    }
    
    // Décoder le payload (sans vérifier la signature - c'est le serveur qui le fait)
    const payload = JSON.parse(atob(parts[1]));
    const now = Math.floor(Date.now() / 1000);
    
    if (payload.exp && payload.exp < now) {
      console.log('[JWT SERVICE] Token expiré');
      return false;
    }
    
    console.log('[JWT SERVICE] Token présent et non expiré (vérification complète côté serveur)');
    return true;
  } catch (error: any) {
    console.error('[JWT SERVICE] Erreur lors de la vérification basique du token:', error.message);
    return false;
  }
};

/**
 * Authentifie l'utilisateur avec Firebase en utilisant les informations du token JWT
 */
export const authenticateWithJwt = async (): Promise<boolean> => {
  console.log('[JWT SERVICE] authenticateWithJwt - Début');
  try {
    const token = getJwtToken();
    if (!token) {
      console.log('[JWT SERVICE] Aucun token disponible');
      return false;
    }

    // Vérification basique côté client (expiration)
    const parts = token.split('.');
    if (parts.length === 3) {
      try {
        const payload = JSON.parse(atob(parts[1]));
        if (auth.currentUser && auth.currentUser.email === payload.email) {
          console.log('[JWT SERVICE] Utilisateur déjà connecté avec le même email');
          return true;
        }
      } catch {
        // Ignore parsing errors, server will validate
      }
    }

    // Envoyer le JWT au serveur pour vérification et obtention du customToken
    console.log('[JWT SERVICE] Envoi du JWT au serveur pour vérification...');
    const { customToken, user } = await callVerifyJwtAndCreateToken(token);
    console.log('[JWT SERVICE] JWT vérifié côté serveur - user:', user.email);
    
    console.log('[JWT SERVICE] Connexion à Firebase avec le custom token...');
    const userCredential = await signInWithCustomToken(auth, customToken);
    console.log('[JWT SERVICE] Connexion Firebase réussie - uid:', userCredential.user.uid);

    console.log('[JWT SERVICE] Mise à jour Firestore...');
    await setDoc(
      doc(db, 'users', userCredential.user.uid),
      {
        email: user.email,
        role: user.role,
        lastLogin: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    console.log('[JWT SERVICE] Mise à jour Firestore réussie');

    return true;
  } catch (error: any) {
    console.error('[JWT SERVICE] Erreur lors de l\'authentification avec JWT:', error);
    console.error('[JWT SERVICE] Stack trace:', error.stack);
    return false;
  }
};

/**
 * Configuration pour les requêtes API avec authentification JWT
 */
export const jwtFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  console.log('[JWT SERVICE] jwtFetch vers:', url);
  const token = getJwtToken();
  if (!token) {
    console.error('[JWT SERVICE] Pas de token JWT disponible pour la requête');
    throw new Error('Pas de token JWT disponible');
  }

  const headers = {
    ...options.headers,
    'Authorization': `Bearer ${token}`
  };

  console.log('[JWT SERVICE] Envoi de la requête avec Authorization header');
  return fetch(url, {
    ...options,
    headers
  });
};

/**
 * Déconnexion et suppression du token JWT
 */
export const logout = async (): Promise<void> => {
  console.log('[JWT SERVICE] Déconnexion - Début');
  clearJwtToken();
  await auth.signOut();
  console.log('[JWT SERVICE] Déconnexion terminée');
};
